import { httpService } from "../../../../base/services/httpService.service";
import { adaptFlatPreviewData } from "./flatPreviewAdapter";
import { PREVIEW_STATUS_CODE } from "./flatPreviewTypes";

/** Flat, single-query endpoint. Replaces the path below for rendering/filling a survey. */
export const FLAT_PREVIEW_PATH = "Survey/GetPreviewDataFlat";
/** Nested endpoint, still live and unchanged. Kept as the fallback behind the feature flag. */
export const LEGACY_PREVIEW_PATH = "Survey/GetPreviewData";

const GENERIC_ERROR = "Something went wrong! Please try again later.";

/**
 * Feature switch for the endpoint swap. `REACT_APP_USE_FLAT_PREVIEW=true` renders from
 * `GetPreviewDataFlat`; anything else (including the variable being absent) keeps the survey
 * renderer on `GetPreviewData`, so a rollback is one env change and a rebuild.
 */
export function isFlatPreviewEnabled() {
   return String(process.env.REACT_APP_USE_FLAT_PREVIEW).toLowerCase() === "true";
}

/**
 * Query string for both endpoints - they take the same parameters.
 *
 * `messageReceiverId` is what the backend records a campaign "survey link open" from, so it is
 * passed exactly where the caller passes `receiverId`, and an empty value is omitted rather than
 * sent as a blank string.
 */
export function buildPreviewParams({ surveyId, languageId, taskId, receiverId } = {}) {
   return new URLSearchParams({
      surveyId,
      ...(languageId && { languageId }),
      ...(taskId && { taskId }),
      ...(receiverId && { messageReceiverId: receiverId })
   });
}

/** Error carrying the envelope so callers can tell "no such survey" from "not published". */
export class PreviewDataError extends Error {
   constructor(message, { statusCode = null, envelope = null } = {}) {
      super(message || GENERIC_ERROR);
      this.name = "PreviewDataError";
      this.statusCode = statusCode;
      this.envelope = envelope;
   }
}

/** First human-readable string an error-shaped payload carries. */
function readErrorMessage(payload) {
   if (!payload) {
      return null;
   }

   if (Array.isArray(payload.errors) && payload.errors.length) {
      return payload.errors.join(" ");
   }

   return payload.message || payload.title || null;
}

/**
 * Normalises anything a rejected request can hand back - an axios error, the envelope itself, or
 * an error we already built - into a `PreviewDataError`.
 */
export function toPreviewDataError(error) {
   if (error instanceof PreviewDataError) {
      return error;
   }

   const envelope = error?.response?.data ?? error;

   return new PreviewDataError(readErrorMessage(envelope) || error?.message || GENERIC_ERROR, {
      statusCode: envelope?.statusCode ?? error?.response?.status ?? null,
      envelope
   });
}

/**
 * Turns a `GetPreviewDataFlat` envelope into the nested `data` shape, or throws.
 *
 * The endpoint reports several outcomes inside the body with HTTP 200 - a missing survey comes
 * back as `isSuccess: true, statusCode: 204, data: null`, and an archived survey requested
 * anonymously as `statusCode: 401` - so the transport status alone is not enough to tell success
 * from failure and `statusCode`/`data` have to be inspected.
 *
 * @param {import("./flatPreviewTypes").ResponseEnvelope} envelope
 * @returns {Object} `data` in the shape `Survey/GetPreviewData` returned
 */
export function resolveFlatPreviewEnvelope(envelope) {
   if (!envelope) {
      throw new PreviewDataError(GENERIC_ERROR, { statusCode: null, envelope });
   }

   const { statusCode, data } = envelope;

   if (statusCode === PREVIEW_STATUS_CODE.NO_CONTENT) {
      throw new PreviewDataError(envelope.message || "Survey not found", { statusCode, envelope });
   }

   if (statusCode === PREVIEW_STATUS_CODE.UNAUTHORIZED) {
      throw new PreviewDataError(
         envelope.message || "Survey is not published. You should be logged in to access this result",
         { statusCode, envelope }
      );
   }

   if (envelope.isSuccess === false || (typeof statusCode === "number" && statusCode >= 400)) {
      throw new PreviewDataError(readErrorMessage(envelope) || GENERIC_ERROR, {
         statusCode,
         envelope
      });
   }

   if (!data) {
      throw new PreviewDataError(readErrorMessage(envelope) || GENERIC_ERROR, {
         statusCode,
         envelope
      });
   }

   return adaptFlatPreviewData(data);
}

/**
 * Fetches the survey preview from `GetPreviewDataFlat` and adapts it to the nested shape.
 *
 * Anonymous, like the endpoint it replaces.
 *
 * @param {{surveyId: string, languageId?: string, taskId?: string, receiverId?: string}} payload
 * @returns {Promise<Object>} resolves with the adapted `data`, rejects with a `PreviewDataError`
 */
export function fetchFlatPreviewData(payload) {
   return httpService
      .get(`${FLAT_PREVIEW_PATH}?${buildPreviewParams(payload).toString()}`)
      .then(resolveFlatPreviewEnvelope, (error) => {
         throw toPreviewDataError(error);
      });
}
